import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Link,
  Navigate,
} from "react-router-dom";

const ASSET_BASE =
  process.env.REACT_APP_ASSET_BASE || `${process.env.PUBLIC_URL || ""}/assets`;

const asset = (p) =>
  `${ASSET_BASE}/${p.split("/").map(encodeURIComponent).join("/")}`;

const HOME_LOGO_VIDEO = asset("logo TM repeat.mp4");
const HOME_HERO_VIDEO = asset("home-boot_1.mp4");
const MARKETING_LOGO_REEL = asset("logo TM repeat.mp4");

/* =========================
   GLOBAL NAV CONFIG
   ========================= */
const NAV_BUTTONS = [
  { key: "home", label: "Home", path: "/home" },
  { key: "marketing", label: "Portfolio", path: "/marketing" },
  { key: "Gallery", label: "Gallery", path: "/Gallery" },
  { key: "social-media", label: "Social Media", path: "/social-media" },
];

function getActiveIndex(pathname) {
  const p = pathname || "/";
  return NAV_BUTTONS.findIndex((b) => {
    if (b.path === "/home") return p === "/" || p.startsWith("/home");
    return p.startsWith(b.path);
  });
}

/* =========================
   GENERIC IMAGE LIGHTBOX
   - Used on Home, Social, Marketing
   ========================= */
function ImageLightbox({ open, src, alt, onClose, actionUrl, actionLabel }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="imgLightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onMouseDown={onClose}
    >
      <div
        className="imgLightboxInner"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="imgLightboxTop">
          <div className="imgLightboxTitle">{alt || "Preview"}</div>

          <div className="imgLightboxTopRight">
            {actionUrl ? (
              <a
                className="imgLightboxAction"
                href={actionUrl}
                target="_blank"
                rel="noreferrer"
              >
                {actionLabel || "OPEN"}
              </a>
            ) : null}

            <button
              type="button"
              className="imgLightboxClose"
              onClick={onClose}
              aria-label="Close preview"
            >
              CLOSE
            </button>
          </div>
        </div>

        <div className="imgLightboxMedia">
          <img src={src} alt={alt || "Preview"} />
        </div>
      </div>
    </div>
  );
}

/* ===========================================
   CHANNEL KNOB (GLOBAL OVERLAY)
   =========================================== */
function ChannelKnob({ powered, docked }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [docked, location.pathname]);

  const isActive = (btnPath) => {
    const p = location.pathname || "/";
    if (btnPath === "/home") return p === "/" || p.startsWith("/home");
    return p.startsWith(btnPath);
  };

  const ITEMS = [
    { label: "Home", path: "/home", pos: "top" },
    { label: "Portfolio", path: "/marketing", pos: "right" },
    { label: "Gallery", path: "/Gallery", pos: "bottom" },
    { label: "Social", path: "/social-media", pos: "left" },
  ];

  const go = (path) => {
    if (!powered) return;
    navigate(path);
  };

  const onEnter = () => {
    if (!docked) return;
    setOpen(true);
  };
  const onLeave = () => {
    if (!docked) return;
    setOpen(false);
  };

  const toggleOpen = () => {
    if (!docked) return;
    setOpen((v) => !v);
  };

  if (!powered) return null;

  if (!docked) {
    return (
      <div className="ckFloat" aria-label="Channel knob navigation">
        <div className="ckKnob" role="navigation" aria-label="Channel knob">
          <div className="ckCore" aria-hidden="true" />
          {ITEMS.map((it) => (
            <button
              key={it.path}
              type="button"
              className={`ckLabel ckLabel--${it.pos} ${
                isActive(it.path) ? "active" : ""
              }`}
              onClick={() => go(it.path)}
              aria-label={`Go to ${it.label}`}
              title={it.label}
            >
              {it.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`ckDock ${open ? "isOpen" : ""}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      aria-label="Channel knob dock"
    >
      <button
        type="button"
        className="ckTab"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-label="Channel knob"
        title="Channel knob"
      >
        CHANNEL KNOB
      </button>

      <div
        className="ckPanel"
        role="region"
        aria-label="Channel knob panel"
        aria-hidden={!open}
      >
        <div className="ckKnob" role="navigation" aria-label="Channel knob">
          <div className="ckCore" aria-hidden="true" />
          {ITEMS.map((it) => (
            <button
              key={it.path}
              type="button"
              className={`ckLabel ckLabel--${it.pos} ${
                isActive(it.path) ? "active" : ""
              }`}
              onClick={() => go(it.path)}
              aria-label={`Go to ${it.label}`}
              title={it.label}
            >
              {it.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===========================================
   CRT LAYOUT
   =========================================== */
function CRTLayout({
  children,
  showPower = false,
  bootSrc = null,
  mode = "scroll",
}) {
  const [powered, setPowered] = useState(!showPower);
  const navigate = useNavigate();
  const location = useLocation();

  const leftButtons = NAV_BUTTONS.slice(0, 3);
  const rightButtons = NAV_BUTTONS.slice(3);

  const isActive = (btnPath) => {
    const p = location.pathname || "/";
    if (btnPath === "/home") return p === "/" || p.startsWith("/home");
    return p.startsWith(btnPath);
  };

  const go = (btnPath) => {
    if (!powered) return;
    navigate(btnPath);
  };

  const handlePowerOn = () => setPowered(true);

  const scrollRef = useRef(null);
  const [knobDocked, setKnobDocked] = useState(false);

  useEffect(() => {
    setKnobDocked(false);

    const el = scrollRef.current;
    if (!el) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = el.scrollTop || 0;
        setKnobDocked(y > 30);
        ticking = false;
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => el.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const css = `
  @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&family=Space+Grotesk:wght@600;700&display=swap');

  :root{
    --bezel1:#e8e2d6; --bezel2:#d7d0c4; --bezel3:#c9c1b5;
    --glass:#07140a; --phosphor:#b5f8c1; --accent:#1dde6a;

    --roomSolid:#000000;
    --ink:#e9edf1; --muted:#b9c5bb; --stroke:#17241a;

    --blk1-bg:#000000;
    --blk2-bg:#000000;
    --blk3-bg:#000000;

    --chromeH: 90px;

    --text:#e9edf1;
    --muted:#a6b0c0;
    --mk-stroke:#1b2230;
    --accent-ink:#052a15;
    --grid: min(1400px, 94%);

    --ck-size: 240px;
    --ck-bottom: 22px;
    --ck-tab-h: 34px;
    --ck-tab-w: 160px;

    --ck-size-m: 150px;
    --ck-bottom-m: 10px;
  }

  *{ box-sizing:border-box; min-width:0; }
  img,video{ max-width:100%; height:auto; }

  /* =====================================================
     IMPORTANT MOBILE FIX:
     - lock horizontal overflow globally
     - ensure scroll areas only scroll vertically
     - avoid vw rounding overflow
     ===================================================== */
  html,body,#root{
    height:100%;
    width:100%;
    overflow:hidden;
    overscroll-behavior:none;
  }
  body{
    margin:0;
    background: var(--roomSolid);
    color:var(--ink);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    overflow-x:hidden;
  }
  .room, .rig, .monitor, .bezel, .screen{ max-width:100%; }
  .room{ width:100%; }

  /* ========= BASE LAYOUT ========= */
  .room{
    height:100svh;
    display:flex;
    align-items:flex-start;
    justify-content:center;
    padding: 6px 0 0;
  }
  .rig{
    width:min(1920px, 100%);
    margin:0 auto;
    padding: 0 4px;
  }

  .monitor{
    width:100%;
    border-radius:18px;
    background: linear-gradient(180deg, var(--bezel1) 0%, var(--bezel2) 100%);
    box-shadow: 0 60px 140px rgba(0,0,0,.9),
                inset 0 7px 0 rgba(255,255,255,.6),
                inset 0 -12px 22px rgba(0,0,0,.12);
    padding:clamp(4px, .6vw, 10px);
    position:relative;
  }
  .bezel{
    border-radius:14px;
    padding:clamp(4px, .7vw, 10px);
    background: linear-gradient(180deg, var(--bezel2), var(--bezel3));
    box-shadow: inset 0 0 0 2px rgba(255,255,255,.5),
                inset 0 0 0 4px rgba(0,0,0,.08);
    position:relative;
  }
  .screw{ position:absolute; width:12px; height:12px; border-radius:50%;
    background: radial-gradient(circle at 30% 30%, #9f978a, #6e675d 60%, #564f46 100%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,.6), inset 0 -1px 0 rgba(0,0,0,.4);
  }
  .screw.tl{ top:10px; left:10px } .screw.tr{ top:10px; right:10px }
  .screw.bl{ bottom:10px; left:10px } .screw.br{ bottom:10px; right:10px }

  .led{ position:absolute; right:18px; bottom:-16px; width:12px; height:12px; border-radius:50%;
    background: radial-gradient(circle at 40% 40%, #f66, #d11 60%, #700 100%); box-shadow: 0 0 8px rgba(209,17,17,.7);
    transition: all .25s ease;
  }
  .led.on{ background: radial-gradient(circle at 40% 40%, #7bff7b, #12c912 60%, #087808 100%);
    box-shadow: 0 0 10px rgba(18,201,18,.7); }

  .stand{
    width: clamp(220px, 24vw, 360px);
    height: 10px;
    margin: 2px auto 0;
    border-radius: 18px / 10px;
    background: linear-gradient(180deg, var(--bezel2), #bfb7aa);
    box-shadow: inset 0 2px 0 rgba(255,255,255,.5), inset 0 -3px 6px rgba(0,0,0,.22), 0 12px 36px rgba(0,0,0,.55);
  }

  .screen{
    position:relative;
    width:100%;
    height: calc(100svh - var(--chromeH));
    min-height: 560px;
    overflow: hidden;
    border-radius: 10px;
    z-index:0;
    background:#000;
  }

  /* keep CRT feel but prevent any sideways scroll */
  .screen-center{
    width:100%;
    height:100%;
    display:grid;
    place-items:center;
    padding: clamp(16px, 3vw, 40px);
    font-family: 'VT323', ui-monospace, Menlo, Consolas, monospace;
    color: var(--phosphor);
    text-align:center;
    position:relative;
    z-index:2;
    overflow-x:hidden;
  }
  .screen-scroll{
    width:100%;
    height:100%;
    overflow-y:auto;
    overflow-x:hidden;          /* ✅ key fix */
    -webkit-overflow-scrolling: touch;
    position:relative;
    z-index:2;
    padding-bottom: 110px;
    touch-action: pan-y;        /* ✅ avoid accidental pan-x */
  }

  .scan{
    pointer-events:none; position:absolute; inset:0;
    background:
      linear-gradient(rgba(200,255,220,.04), rgba(200,255,220,0) 2px),
      repeating-linear-gradient(transparent 0 2px, rgba(180,255,210,.035) 3px, transparent 4px);
    mix-blend-mode:screen; opacity:.14;
    z-index:1;
  }

  .prepower{ position:absolute; inset:0; z-index:0; }
  .videoFill{
    position:absolute; inset:0;
    width:100%; height:100%;
    object-fit: cover; object-position: center center;
    background:#000; display:block;
  }
  .powerOn{
    font-family:'VT323', ui-monospace, monospace; font-size: clamp(22px, 3vw, 32px); letter-spacing:1px;
    color: var(--glass); background: var(--accent); border:4px solid var(--accent); border-radius:999px;
    padding:16px 28px; cursor:pointer; box-shadow: 0 0 28px rgba(29,222,106,.42);
    transition: transform .12s ease, filter .12s ease, box-shadow .12s ease; position:absolute; left:50%; top:50%;
    transform:translate(-50%, -50%); z-index:3;
  }
  .powerOn:hover{ transform: translateY(-3px) translateX(-50%) scale(1.05); filter: brightness(1.05);
    box-shadow: 0 0 42px rgba(28,222,106,.55); }

  .sideDock{
    position:absolute;
    top:50%;
    transform: translateY(-50%);
    display:flex;
    flex-direction:column;
    gap:10px;
    z-index:5;
    pointer-events:auto;
  }
  .sideDock.left{ left: 0; }
  .sideDock.right{ right: 0; }

  .ctrlBtn{
    display:flex; align-items:center; gap:10px;
    background: linear-gradient(180deg, #bdb6ac, #a89f90);
    border: 2px solid #5b554c; border-radius: 12px;
    padding: 8px 10px;
    min-width: 170px;
    max-width: 220px;          /* ✅ prevents extra-wide buttons from forcing overflow */
    cursor: pointer;
    box-shadow: inset 0 2px 0 rgba(255,255,255,.4), inset 0 -3px 6px rgba(0,0,0,.18), 0 6px 18px rgba(0,0,0,.35);
    transition: transform .20s ease, filter .12s ease, box-shadow .12s ease, opacity .2s ease;
    user-select:none; opacity: 0.95;
    white-space: nowrap;
  }
  .sideDock.left  .ctrlBtn{ transform: translateX(calc(-100% + 28px)); border-top-left-radius: 0; border-bottom-left-radius: 0; }
  .sideDock.right .ctrlBtn{ transform: translateX(calc(100% - 28px)); border-top-right-radius: 0; border-bottom-right-radius: 0; }
  .sideDock .ctrlBtn:hover,
  .sideDock .ctrlBtn:focus,
  .sideDock .ctrlBtn:focus-visible,
  .sideDock .ctrlBtn:focus-within{
    transform: translateX(0);
    opacity: 1;
  }
  .ctrlBtn[aria-disabled="true"]{ filter: grayscale(0.6) brightness(0.85); cursor:not-allowed; pointer-events:none; }
  .ctrlBtn:active{ transform: translateY(2px) }

  .lamp{
    width: 14px; height:14px; border-radius:50%;
    background: radial-gradient(circle at 40% 40%, #6c6f63, #333029 70%, #1a1713 100%);
    box-shadow: inset 0 1px 1px rgba(255,255,255,.25), inset 0 -2px 2px rgba(0,0,0,.4);
    flex:none;
  }
  .ctrlBtn.active .lamp{
    background: radial-gradient(circle at 40% 40%, #9affb8, #17cc68 60%, #0d7a3a 100%);
    box-shadow: 0 0 10px rgba(29,222,106,.65), 0 0 26px rgba(29,222,106,.35),
                inset 0 1px 1px rgba(255,255,255,.35), inset 0 -2px 2px rgba(0,0,0,.35);
  }
  .ctrlText{
    font: 700 14px/1.2 ui-sans-serif, system-ui, Segoe UI, Inter, Arial;
    color: #2e2a23;
    text-transform: uppercase;
    letter-spacing: .06em;
    overflow:hidden;
    text-overflow: ellipsis;
  }
/* ==========================
   MOBILE: hide side tabs
   ========================== */
@media (max-width: 720px){
  .sideDock{ display:none !important; }
}

  /* ==========================
     CHANNEL KNOB STYLES
     ========================== */
  .ckFloat{
    position: fixed;
    left: 50%;
    bottom: var(--ck-bottom);
    transform: translateX(-50%);
    z-index: 1500;
    pointer-events: auto;
  }
  .ckKnob{
    width: var(--ck-size);
    height: var(--ck-size);
    position: relative;
    display: grid;
    place-items: center;
    filter: drop-shadow(0 18px 40px rgba(0,0,0,.65));
  }
  .ckCore{
    width: 58%;
    height: 58%;
    border-radius: 50%;
    background:
      radial-gradient(circle at 30% 25%, rgba(255,255,255,.28), rgba(255,255,255,0) 40%),
      radial-gradient(circle at 50% 60%, #3b3b3b, #121212 62%, #070707 100%);
    border: 2px solid rgba(255,255,255,.18);
    box-shadow:
      inset 0 10px 18px rgba(255,255,255,.08),
      inset 0 -14px 22px rgba(0,0,0,.62),
      0 0 18px rgba(29,222,106,.25);
    position: relative;
  }
  .ckCore::after{
    content:"";
    position:absolute;
    inset: 12%;
    border-radius: 50%;
    background:
      radial-gradient(circle at 40% 30%, rgba(255,255,255,.18), rgba(255,255,255,0) 55%),
      radial-gradient(circle at 50% 55%, rgba(29,222,106,.22), rgba(0,0,0,0) 60%);
    border: 1px solid rgba(255,255,255,.10);
  }

  .ckLabel{
    position:absolute;
    appearance:none;
    border:none;
    cursor:pointer;
    pointer-events:auto;
    padding: 10px 12px 9px;
    min-width: 108px;
    background-color: #d6d7cd;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,.35), rgba(255,255,255,0) 42%),
      repeating-linear-gradient(0deg, rgba(0,0,0,.06) 0 2px, rgba(0,0,0,0) 2px 6px);
    border: 2px solid rgba(0,0,0,.7);
    border-radius: 10px;
    box-shadow:
      0 10px 22px rgba(0,0,0,.55),
      inset 0 1px 0 rgba(255,255,255,.35);
    font: 900 12px/1 Inter;
    letter-spacing: .14em;
    text-transform: uppercase;
    color: #111;
    text-align:center;
    transition: transform .14s ease, filter .14s ease, box-shadow .14s ease;
    user-select:none;
  }
  .ckLabel:hover{
    transform: translate(-50%, -50%) scale(1.03);
    filter: brightness(1.05);
    box-shadow:
      0 14px 28px rgba(0,0,0,.65),
      0 0 18px rgba(29,222,106,.18);
  }
  .ckLabel.active{
    background-color: #ffd84d;
    border-color: #050505;
    box-shadow:
      0 14px 28px rgba(0,0,0,.65),
      0 0 18px rgba(255,216,77,.35);
  }
  .ckLabel--top{ top: 6%; left: 50%; transform: translate(-50%, -50%) rotate(-2deg); }
  .ckLabel--right{ top: 50%; left: 94%; transform: translate(-50%, -50%) rotate(2deg); }
  .ckLabel--bottom{ top: 94%; left: 50%; transform: translate(-50%, -50%) rotate(1deg); }
  .ckLabel--left{ top: 50%; left: 6%; transform: translate(-50%, -50%) rotate(-1deg); }

  .ckDock{
    position: fixed;
    left: 50%;
    bottom: calc(10px + env(safe-area-inset-bottom, 0px));
    transform: translateX(-50%);
    z-index: 1500;
    pointer-events: auto;
    width: var(--ck-tab-w);
    height: var(--ck-tab-h);
  }
  .ckTab{
    position: relative;
    z-index: 2;
    appearance:none;
    border: 2px solid rgba(0,0,0,.7);
    background: linear-gradient(180deg, #bdb6ac, #a89f90);
    border-radius: 999px;
    width: var(--ck-tab-w);
    height: var(--ck-tab-h);
    cursor: pointer;
    font: 900 11px/1 Inter;
    letter-spacing: .18em;
    text-transform: uppercase;
    color: #2e2a23;
    box-shadow:
      0 12px 26px rgba(0,0,0,.55),
      inset 0 1px 0 rgba(255,255,255,.35);
  }
  .ckTab:hover{ filter: brightness(1.05); }

  .ckPanel{
    position: absolute;
    left: 50%;
    bottom: calc(var(--ck-tab-h) + 8px);
    transform: translateX(-50%) translateY(24px);
    opacity: 0;
    pointer-events: none;
    width: var(--ck-size);
    height: var(--ck-size);
    display:grid;
    place-items:center;
    transition: transform .22s ease, opacity .22s ease;
    z-index: 1;
  }
  .ckDock.isOpen .ckPanel{
    transform: translateX(-50%) translateY(0);
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: 720px){
    .ckFloat{ bottom: var(--ck-bottom-m); }
    .ckKnob{ width: var(--ck-size-m); height: var(--ck-size-m); }
    .ckPanel{ width: var(--ck-size-m); height: var(--ck-size-m); }
    .ckLabel{ min-width: 86px; padding: 8px 10px 7px; font-size: 10px; letter-spacing: .16em; }
    .ckDock{ bottom: calc(6px + env(safe-area-inset-bottom, 0px)); }
    .ckTab{ width: 150px; height: 32px; font-size: 10px; }
  }

  /* ==========================
     NEW IMAGE LIGHTBOX (GLOBAL)
     ========================== */
  .imgLightbox{
    position: fixed;
    inset: 0;
    z-index: 2600;
    background: rgba(0,0,0,.84);
    display: grid;
    place-items: center;
    padding: 16px;
  }
  .imgLightboxInner{
    width: min(1200px, 96vw);
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,.14);
    background: #000;
    box-shadow: 0 30px 120px rgba(0,0,0,.95);
  }
  .imgLightboxTop{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255,255,255,.10);
    background: rgba(0,0,0,.6);
  }
  .imgLightboxTitle{
    font: 800 14px/1.2 Inter;
    color: #e9edf1;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .imgLightboxTopRight{
    display:flex;
    gap:10px;
    align-items:center;
    justify-content:flex-end;
  }
  .imgLightboxAction{
    text-decoration:none;
    border: 1px solid rgba(255,255,255,.18);
    background: rgba(0,0,0,.55);
    color:#e9edf1;
    border-radius: 12px;
    padding: 10px 12px;
    font: 900 12px/1 Inter;
    letter-spacing:.12em;
    text-transform: uppercase;
  }
  .imgLightboxAction:hover{ filter: brightness(1.08); }
  .imgLightboxClose{
    appearance:none;
    border: 1px solid rgba(255,255,255,.18);
    background: rgba(0,0,0,.55);
    color:#e9edf1;
    border-radius: 12px;
    padding: 10px 12px;
    font: 900 12px/1 Inter;
    letter-spacing:.12em;
    text-transform: uppercase;
    cursor:pointer;
  }
  .imgLightboxClose:hover{ filter: brightness(1.08); }
  .imgLightboxMedia{
    width:100%;
    height: min(78vh, 820px);
    background:#000;
    display:block;
  }
  .imgLightboxMedia img{
    width:100%;
    height:100%;
    object-fit: contain;
    display:block;
  }

  /* ===== HOME ===== */
  .h_wrap{ min-height:100%; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial; color:#e8efe9; }
  .h_hero{
    position: relative; width: 100%; min-height: calc(100svh - var(--chromeH));
    display: grid; place-items: center; overflow: hidden; z-index:2; background:#000;
  }
  .h_bgVideo{
    position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:center 60%;
    background:#000; display:block; z-index:0; pointer-events:none;
  }
  .h_overlay{ position:relative; z-index:2; text-align:center; padding: 0 4vw; }
  .h_welcome{
    font-family: 'VT323', ui-monospace, monospace; font-size: clamp(42px, 8vw, 84px);
    letter-spacing:.5px; color:#b5f8c1;
    text-shadow: 0 0 10px rgba(166,247,179,.35), 0 0 22px rgba(29,222,106,.25);
    transition: opacity 1.6s ease;
  }
  .h_welcome.fade{ opacity:0; }
  .h_cursor{ display:inline-block; width:.6ch; color:#7dffb2; animation: h_blink 1s steps(2,end) infinite; }
  @keyframes h_blink{ 0%,49%{opacity:1} 50%,100%{opacity:0} }

  .h_scroll{
    position:absolute; top:50%; transform: translateY(-50%);
    display:flex; flex-direction:column; align-items:center; gap:8px;
    filter: drop-shadow(0 6px 16px rgba(0,0,0,.45));
    pointer-events:auto; cursor:pointer; user-select:none; z-index:2;
  }
  .h_scroll.left{ left: clamp(10px, 1.8vw, 18px); }
  .h_scroll.right{ right: clamp(10px, 1.8vw, 18px); }
  .h_plate{
    background: linear-gradient(180deg, #2a2c27, #191c18);
    border:1px solid rgba(0,0,0,.6);
    border-radius:10px; padding:6px 10px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.06), 0 6px 16px rgba(0,0,0,.35);
  }
  .h_sign{
    position:relative;
    font: 900 clamp(12px, 1.7vw, 16px)/1.1 "Space Grotesk", Inter;
    letter-spacing:.18em; color: #9affb8; text-transform: uppercase;
    text-shadow: 0 0 6px rgba(154,255,184,.85), 0 0 14px rgba(29,222,106,.65), 0 0 26px rgba(29,222,106,.35);
    animation: neonFlicker 3.8s infinite;
  }
  @keyframes neonFlicker{
    0%, 6%{ opacity:.92; filter: brightness(1) }
    7%{ opacity:.45; filter: brightness(.7) }
    8%,11%{ opacity:.95; filter: brightness(1.05) }
    12%{ opacity:.6; filter: brightness(.8) }
    13%,100%{ opacity:.98; filter: brightness(1.05) }
  }
  .h_arrow{
    width:20px; height:20px;
    border-right:3px solid #9affb8; border-bottom:3px solid #9affb8;
    transform: rotate(45deg); opacity:.9;
    box-shadow: 0 0 8px rgba(154,255,184,.6), 0 0 18px rgba(29,222,106,.35);
  }
  .h_arrow.a1{ animation: h_bounce 1.2s ease-in-out infinite .0s; }
  .h_arrow.a2{ animation: h_bounce 1.2s ease-in-out infinite .15s; }
  .h_arrow.a3{ animation: h_bounce 1.2s ease-in-out infinite .3s; }
  @keyframes h_bounce{
    0%,100%{ transform: translateY(0) rotate(45deg); opacity:.7 }
    50%{ transform: translateY(8px) rotate(45deg); opacity:1 }
  }

  .h_audioWrap{
    position:absolute; right: clamp(10px, 1.6vw, 18px); bottom: clamp(10px, 1.6vw, 18px);
    display:flex; align-items:center; gap:10px; z-index:2; background: rgba(0,0,0,.55); backdrop-filter: blur(6px);
    border: 1px solid rgba(255,255,255,.15); border-radius: 12px; padding: 8px 10px;
  }
  .h_audioBtn{
    appearance:none; border:none;
    background: linear-gradient(180deg, #2a2f2a, #141714);
    color:#cfead7; font-size: 16px; line-height: 1;
    padding: 8px 10px; border-radius: 10px; cursor: pointer;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.06);
  }
  .h_audioBtn:hover{ filter: brightness(1.05); }
  .h_audioSlider{ width: 120px; accent-color: #1dde6a; }
  @media (max-width: 640px){ .h_audioSlider{ width: 90px; } }

  .h_outer{ width:min(1600px, 92%); margin: 22px auto 22px; }
  .h_block{
    border:1px solid var(--stroke); border-radius: 16px;
    padding: clamp(14px, 2.4vw, 22px);
    box-shadow: 0 18px 50px rgba(0,0,0,.7);
  }
  .h_block + .h_block{ margin-top: 14px; }
  .h_block--mint{ background: var(--blk1-bg); }
  .h_block--slate{ background: var(--blk2-bg); }
  .h_block--ink { background: var(--blk3-bg); }

  .h_centerNote{
    border:1px dashed rgba(255,255,255,.16);
    background: radial-gradient(circle at top, rgba(29,222,106,.16), rgba(0,0,0,.9));
    border-radius: 16px;
    padding: clamp(18px, 2.8vw, 26px);
    color:#d9e5db;
    text-align:center;
    display:grid; place-items:center;
    box-shadow: inset 0 0 0 1px rgba(0,0,0,.35), 0 14px 30px rgba(0,0,0,.7);
  }
  .h_centerNote strong{
    display:block;
    font: 900 clamp(18px, 2.4vw, 22px)/1.3 "Space Grotesk", Inter;
    letter-spacing:.04em;
    margin-bottom: 6px;
    text-transform: uppercase;
    color:#b5f8c1;
  }
  .h_centerNote p{
    margin:0;
    max-width: 68ch;
    font: 600 clamp(13px, 1.6vw, 15px)/1.7 Inter;
    color:#cfe0d4;
  }

  .h_carouselWrap{ margin-top: 16px; display:flex; flex-direction:column; align-items:center; gap: 14px; }
  .h_carousel{ width:100%; display:flex; align-items:center; justify-content:center; gap:10px; }
  .h_carFrame{ position:relative; display:flex; flex-direction:column; justify-content:center; }
  .h_carFrame--center{ flex:0 0 50%; max-width: 840px; z-index:2; }
  .h_carFrame--side{ flex:0 0 20%; max-width: 260px; opacity:.4; transform: scale(.9); filter: blur(0.2px); transition: opacity .2s ease, transform .2s ease; }
  .h_imgCard{
    position:relative; border-radius:14px; overflow:hidden; border:none; background:#000;
    height: clamp(220px, 40vh, 420px); box-shadow: 0 16px 38px rgba(0,0,0,.6);
  }
  .h_imgCard img{ width:100%; height:100%; object-fit:contain; background:#000; display:block; }
  .h_imgCard--carousel{ display:flex; flex-direction:column; justify-content:center; }
  .h_imgCard--side img{ object-fit:cover; }
  .h_imgClickable{ cursor: pointer; }

  .h_carCaption{ margin-top:6px; font: 600 13px/1.5 Inter; color:#cfe0d4; text-align:center; opacity:.9; }
  .h_carCaption span{ color:#b5f8c1; font-weight:700; text-transform:uppercase; letter-spacing:.09em; font-size:11px; display:block; margin-bottom:4px; }

  .h_carBtn{
    appearance:none; border:none; border-radius:999px; padding:10px 12px; cursor:pointer;
    background: radial-gradient(circle at 30% 20%, #f7fff9, #1dde6a);
    color:#02140a; font-size:18px; display:flex; align-items:center; justify-content:center;
    box-shadow: 0 0 12px rgba(29,222,106,.7), 0 0 32px rgba(29,222,106,.35);
    transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
  }
  .h_carBtn:hover{ transform: translateY(-1px) scale(1.04); filter: brightness(1.05); box-shadow: 0 0 16px rgba(29,222,106,.9), 0 0 40px rgba(29,222,106,.5); }
  .h_carBtn:active{ transform: translateY(1px) scale(0.97); box-shadow: 0 0 10px rgba(29,222,106,.6), 0 0 26px rgba(29,222,106,.3); }

  .h_carDots{ display:flex; gap:6px; justify-content:center; align-items:center; margin-top:2px; }
  .h_carDot{ width:9px; height:9px; border-radius:50%; background: #1b2a21; border:1px solid #1dde6a; opacity:.4; cursor:pointer; transition: opacity .15s ease, transform .15s ease, box-shadow .15s ease, background .15s ease; }
  .h_carDot.active{ opacity:1; background:#1dde6a; transform: scale(1.15); box-shadow: 0 0 10px rgba(29,222,106,.9); }
  @media (max-width: 880px){
    .h_carFrame--side{ display:none; }
    .h_carFrame--center{ flex:1 1 auto; max-width: 100%; }
  }

  /* ===== Gallery PAGE ===== */
  .cg-wrap{ min-height:100%; display:flex; flex-direction:column; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial; color: var(--text); }
  .cg-hero{ display:grid; place-items:center; padding: 34px 0 10px; text-align:center; }
  .cg-hero-inner{ width:var(--grid); display:grid; gap:10px; margin-inline:auto; }
  .cg-h1{ font: 800 clamp(32px,5.8vw,60px)/1.1 "Space Grotesk", Inter; letter-spacing:-.02em; margin:0; background: linear-gradient(180deg, #ffffff, #d8e3d9); color: transparent; background-clip:text; }
  .cg-sub{ margin:0; max-width: 78ch; margin-inline:auto; color: var(--muted); font: 500 15px/1.7 Inter; }
  .cg-shell{ width:var(--grid); margin: 18px auto 30px; overflow-x:hidden; } /* ✅ */

  .cg-controls{ display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; margin-bottom:12px; }
  .cg-pillRow{ display:flex; flex-wrap:wrap; gap:8px; }
  .cg-pill{
    appearance:none;
    border:1px solid var(--mk-stroke);
    background: rgba(0,0,0,.65);
    color: var(--text);
    border-radius: 999px;
    padding: 9px 12px;
    font: 800 12px/1 Inter;
    letter-spacing:.08em;
    text-transform:uppercase;
    cursor:pointer;
    transition: transform .12s ease, filter .12s ease, border-color .12s ease, box-shadow .12s ease, opacity .12s ease;
    opacity:.88;
  }
  .cg-pill:hover{ transform: translateY(-1px); filter: brightness(1.05); opacity:1; }
  .cg-pill.active{ border-color: rgba(29,222,106,.9); box-shadow: 0 0 16px rgba(29,222,106,.22); opacity:1; }
  .cg-count{ color: var(--muted); font: 700 13px/1.2 Inter; }

  .cg-grid{
    display:grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    width:100%;
  }
  .cg-card{
    position:relative;
    border-radius: 16px;
    overflow:hidden;
    border:1px solid #111722;
    background:#000;
    box-shadow: 0 18px 55px rgba(0,0,0,.7);
    cursor:pointer;
    min-height: 160px;
    transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease;
  }
  .cg-card:hover{ transform: translateY(-3px); border-color: rgba(255,255,255,.22); box-shadow: 0 28px 80px rgba(0,0,0,.85); }
  .cg-media{ width:100%; height:100%; object-fit:cover; display:block; }

  .cg-overlay{
    position:absolute;
    inset:auto 10px 10px 10px;
    background: rgba(0,0,0,.62);
    border: 1px solid rgba(255,255,255,.12);
    border-radius: 12px;
    padding: 10px 10px 9px;
    backdrop-filter: blur(6px);
  }
  .cg-tag{ margin:0 0 4px; font: 900 11px/1.2 Inter; letter-spacing:.14em; text-transform:uppercase; color:#b5f8c1; }
  .cg-title{ margin:0; font: 700 13px/1.4 Inter; color:#dfe7e0; }

  .cg-span-3{ grid-column: span 3; }
  .cg-span-4{ grid-column: span 4; }
  .cg-span-6{ grid-column: span 6; }
  .cg-span-12{ grid-column: span 12; }

  @media (max-width: 1100px){
    .cg-grid{ grid-template-columns: repeat(8, minmax(0, 1fr)); }
    .cg-span-6{ grid-column: span 8; }
    .cg-span-4{ grid-column: span 4; }
    .cg-span-3{ grid-column: span 4; }
    .cg-span-12{ grid-column: span 8; }
  }
  @media (max-width: 720px){
    .cg-grid{ grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .cg-span-12, .cg-span-6, .cg-span-4, .cg-span-3{ grid-column: span 4; }
    .cg-card{ min-height: 220px; }
  }

  /* Gallery Lightbox */
  .cg-lightbox{ position:fixed; inset:0; z-index:2000; background: rgba(0,0,0,.82); display:grid; place-items:center; padding: 18px; }
  .cg-lightboxInner{ width:min(1200px, 96vw); border-radius: 18px; overflow:hidden; border:1px solid rgba(255,255,255,.14); background:#000; box-shadow: 0 30px 120px rgba(0,0,0,.95); }
  .cg-lightboxTop{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding: 12px 14px; border-bottom:1px solid rgba(255,255,255,.10); background: rgba(0,0,0,.6); }
  .cg-lightboxTopLeft{ display:grid; gap:2px; }
  .cg-lightboxKicker{ margin:0; font: 900 11px/1.2 Inter; letter-spacing:.14em; text-transform:uppercase; color:#b5f8c1; }
  .cg-lightboxTitle{ margin:0; font: 800 14px/1.2 Inter; color:#e9edf1; }
  .cg-close{
    appearance:none; border:1px solid rgba(255,255,255,.18); background: rgba(0,0,0,.55);
    color:#e9edf1; border-radius: 12px; padding: 10px 12px;
    font: 900 12px/1 Inter; letter-spacing:.12em; text-transform:uppercase; cursor:pointer;
  }
  .cg-close:hover{ filter: brightness(1.08); }
  .cg-lightboxMedia{ width:100%; height: min(70vh, 720px); background:#000; display:block; }
  .cg-lightboxMedia img, .cg-lightboxMedia video{ width:100%; height:100%; object-fit:contain; display:block; }

  /* ===== SOCIAL MEDIA PAGE ===== */
  .sm-wrap{ min-height:100%; display:flex; flex-direction:column; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial; color: var(--text); }
  .sm-hero{ display:grid; place-items:center; padding: 32px 0 10px; text-align:center; }
  .sm-hero-inner{ width:var(--grid); display:grid; gap:12px; margin-inline:auto; }
  .sm-h1{ font: 800 clamp(32px,5.8vw,60px)/1.1 "Space Grotesk", Inter; letter-spacing:-.02em; margin:0; background: linear-gradient(180deg, #ffffff, #d8e3d9); color: transparent; background-clip:text; text-align:center; }
  .sm-sub{ margin:0; max-width: 72ch; margin-inline:auto; color: var(--muted); font: 500 15px/1.7 Inter; text-align:center; }
  .sm-section{ width:var(--grid); margin: 26px auto 0; padding-bottom:6px; overflow-x:hidden; } /* ✅ */

  .sm-carouselShell{ background:#000; border:1.5px solid var(--mk-stroke); border-radius: 18px; box-shadow: 0 22px 70px rgba(0,0,0,.75); overflow:hidden; }
  .sm-carouselTop{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding: 14px 14px 0; }
  .sm-carouselTitle{ margin:0; font: 800 18px/1.2 Inter; color: var(--text); text-align:left; }
  .sm-carouselHint{ margin:0; font: 600 12px/1.2 Inter; color: var(--muted); text-align:right; }

  .sm-carousel{ position:relative; padding: 12px 14px 14px; }
  .sm-carouselRow{
    display:grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(220px, 1fr);
    gap: 12px;
    overflow-x:auto;
    overflow-y:hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 6px;
  }
  .sm-tile{
    scroll-snap-align: start;
    border-radius: 14px;
    overflow:hidden;
    border: 1px solid #111722;
    background:#060708;
    position:relative;
    cursor:pointer;
    transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease, opacity .16s ease;
    min-height: 220px;
    display:flex;
    align-items:center;
    justify-content:center;
    text-decoration:none;
    color: inherit;
  }
  .sm-tile:hover{ transform: translateY(-3px); border-color: rgba(255,255,255,.25); box-shadow: 0 18px 50px rgba(0,0,0,.85); }
  .sm-tile img{ width:100%; height:100%; object-fit:cover; display:block; }
  .sm-tileOverlay{
    position:absolute;
    inset:auto 10px 10px 10px;
    background: rgba(0,0,0,.62);
    border: 1px solid rgba(255,255,255,.12);
    border-radius: 12px;
    padding: 10px 10px 9px;
    backdrop-filter: blur(6px);
  }
  .sm-tileTag{ font: 800 11px/1.2 Inter; letter-spacing:.12em; text-transform: uppercase; color: #b5f8c1; margin:0 0 4px; }
  .sm-tileCap{ margin:0; color:#dfe7e0; font: 600 13px/1.45 Inter; }

  .sm-actions{ display:flex; gap:10px; justify-content:flex-end; padding: 0 14px 14px; }
  .sm-btn{
    appearance:none; border:1px solid #1a2a20; background:#0e1511; color: var(--text);
    border-radius: 12px; padding: 10px 12px; font: 800 13px/1 Inter; cursor:pointer;
    transition: transform .12s ease, filter .12s ease, box-shadow .12s ease;
  }
  .sm-btn:hover{ transform: translateY(-1px); filter: brightness(1.06); box-shadow: 0 0 16px rgba(29,222,106,.15); }
  .sm-btnPrimary{ background: var(--accent); color: var(--accent-ink); border-color:#1dd86a; box-shadow: 0 10px 26px rgba(29,222,106,.18); }

  .sm-footer{
    width:var(--grid);
    margin: 28px auto 34px;
    display:flex;
    align-items:center;
    justify-content:center;
    gap: 10px;
    flex-wrap:wrap;
    text-align:center;
  }
  .sm-pill{
    display:inline-flex; align-items:center; gap:10px;
    padding: 12px 14px;
    border-radius: 999px;
    border:1px solid var(--mk-stroke);
    background: rgba(0,0,0,.7);
    color: var(--text);
    text-decoration:none;
    font: 800 13px/1 Inter;
    box-shadow: 0 12px 34px rgba(0,0,0,.55);
    transition: transform .12s ease, filter .12s ease, box-shadow .12s ease, border-color .12s ease;
  }
  .sm-pill:hover{ transform: translateY(-2px); filter: brightness(1.06); border-color: rgba(255,255,255,.25); box-shadow: 0 18px 48px rgba(0,0,0,.8); }
  .sm-pillDot{ width:9px; height:9px; border-radius:50%; background: var(--accent); box-shadow: 0 0 10px rgba(29,222,106,.55); }

  .sm-contact{ width:var(--grid); margin: 10px auto 36px; display:grid; gap: 14px; text-align:center; }
  .sm-contactCard{
    background:#000;
    border:1.5px solid var(--mk-stroke);
    border-radius: 18px;
    padding: 18px 18px 20px;
    box-shadow: 0 22px 70px rgba(0,0,0,.75);
    display:grid;
    gap: 10px;
    place-items:center;
  }
  .sm-contactTitle{ margin:0; font: 900 22px/1.15 "Space Grotesk", Inter; color: var(--text); letter-spacing:-.01em; }
  .sm-contactSub{ margin:0; max-width: 72ch; color: var(--muted); font: 500 14px/1.7 Inter; }
  .sm-contactActions{ display:flex; gap: 10px; flex-wrap:wrap; justify-content:center; margin-top: 6px; }
  .sm-contactBtn{
    display:inline-flex; align-items:center; justify-content:center; gap:10px;
    padding: 12px 14px;
    border-radius: 12px;
    border:1px solid #1a2a20;
    background:#0e1511;
    color: var(--text);
    text-decoration:none;
    font: 900 13px/1 Inter;
    letter-spacing:.08em;
    text-transform:uppercase;
    cursor:pointer;
    transition: transform .12s ease, filter .12s ease, box-shadow .12s ease, border-color .12s ease;
    box-shadow: 0 14px 34px rgba(0,0,0,.55);
  }
  .sm-contactBtn:hover{ transform: translateY(-2px); filter: brightness(1.06); border-color: rgba(255,255,255,.22); box-shadow: 0 18px 46px rgba(0,0,0,.8); }
  .sm-contactBtnPrimary{ background: var(--accent); color: var(--accent-ink); border-color:#1dd86a; box-shadow: 0 10px 26px rgba(29,222,106,.18); }

  /* ===== PORTFOLIO / MARKETING ===== */
  .mk-wrap{ min-height:100%; display:flex; flex-direction:column; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial; color: var(--text) }
  .mk-nav{ width:100%; display:flex; justify-content:center; backdrop-filter: blur(6px); position:sticky; top:0; z-index:1; background: rgba(0,0,0,.65); border-bottom:1px solid var(--mk-stroke); }
  .mk-nav-inner{ width:var(--grid); display:flex; align-items:center; gap:16px; padding:12px 2px; min-width:0; }
  .mk-home{ color: var(--accent); text-decoration:none; padding:8px 12px; border-radius:10px; border:1px solid #1a2a20; background:#0e1511; font: 700 14px/1 Inter; }
  .mk-spacer{ flex:1; min-width: 8px; }
  .mk-link{ color: var(--muted); text-decoration:none; padding:8px 10px; border-radius:8px; font: 500 14px/1.2 Inter; }
  .mk-link:hover{ color: var(--text); }
  .mk-cta{ background: var(--accent); color: var(--accent-ink); border:1px solid #1dd86a; border-radius: 999px; padding:10px 16px; font: 700 14px/1 Inter; box-shadow: 0 8px 30px rgba(29,222,106,.25); text-decoration:none; }

  .mk-hero{
  display:grid;
  place-items:center;
  padding: 80px 0 0;
  text-align:center;
}
  .mk-hero-inner{ width:var(--grid); display:grid; gap:14px; text-align:center; margin-inline:auto; }
  .mk-h1{ font: 800 clamp(32px,5.8vw,60px)/1.1 "Space Grotesk", Inter; letter-spacing:-.02em; margin:0; background: linear-gradient(180deg, #ffffff, #d8e3d9); color: transparent; background-clip:text; }
  .comicHead{ display:inline-block; font-family: "VT323", ui-monospace, monospace; text-transform: uppercase; letter-spacing: .08em; padding: .35em .55em .3em; background: #ffd84d; color: #111; border-radius: 12px; border: 3px solid #050505; box-shadow: 0 4px 0 #050505, 0 10px 26px rgba(0,0,0,.45); }
.mk-heroSub{
  max-width:820px;
  margin:10px auto 0;
  color:var(--muted);
  font:600 16px/1.7 Inter;
}

.mk-servicesGrid{
  width:var(--grid);
  display:grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  gap:22px;
  margin:36px auto 0;
}

.mk-serviceColumn{
  position:relative;
  overflow:hidden;
  background:
    radial-gradient(circle at 20% 0%, rgba(29,222,106,.18), transparent 35%),
    linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.02));
  border:1px solid rgba(255,255,255,.14);
  border-radius:24px;
  padding:22px;
  box-shadow:
    0 24px 70px rgba(0,0,0,.75),
    inset 0 1px 0 rgba(255,255,255,.16);
  backdrop-filter:blur(10px);
  transform:translateY(0);
  animation:mkFloat 4.8s ease-in-out infinite;
}

.mk-serviceColumn:nth-child(2){ animation-delay:.35s; }
.mk-serviceColumn:nth-child(3){ animation-delay:.7s; }

.mk-serviceColumn::before{
  content:"";
  position:absolute;
  width:160px;
  height:160px;
  border-radius:50%;
  right:-55px;
  top:-65px;
  background:radial-gradient(circle, rgba(255,216,77,.22), transparent 70%);
  animation:mkBubble 7s ease-in-out infinite;
}

.mk-serviceColumn::after{
  content:"";
  position:absolute;
  width:90px;
  height:90px;
  border-radius:50%;
  left:-28px;
  bottom:-30px;
  background:radial-gradient(circle, rgba(29,222,106,.2), transparent 70%);
  animation:mkBubble 6s ease-in-out infinite reverse;
}

@keyframes mkFloat{
  0%,100%{ transform:translateY(0); }
  50%{ transform:translateY(-8px); }
}

@keyframes mkBubble{
  0%,100%{ transform:translate(0,0) scale(1); opacity:.75; }
  50%{ transform:translate(-12px,18px) scale(1.15); opacity:1; }
}

.mk-serviceTitle{
  position:relative;
  z-index:1;
  display:inline-block;
  margin:0 0 16px;
  padding:8px 12px 7px;
  background:#ffd84d;
  color:#111;
  border:3px solid #050505;
  border-radius:12px;
  box-shadow:0 4px 0 #050505;
  font:900 13px/1.15 "Space Grotesk", Inter;
  text-transform:uppercase;
  letter-spacing:.08em;
}

.mk-serviceColumn .mk-bullets{
  position:relative;
  z-index:1;
  list-style:none;
  padding:0;
  margin:0;
  display:flex;
  flex-direction:column;
  gap:13px;
}

.mk-serviceColumn .mk-bullets li{
  display:flex;
  align-items:flex-start;
  gap:10px;
  text-align:left;
  color:var(--text);
  font:700 15px/1.55 Inter;
}

@media (max-width:980px){
  .mk-servicesGrid{
    grid-template-columns:1fr;
  }
}
  /* ===== CORE STRENGTHS GRID (3 TOP / 2 BOTTOM — FIXED) ===== */
.mk-strengthsGrid{
  display:grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto;
  gap:18px;
  margin-top:24px;
}

/* Top row */
/* ===== CORE STRENGTHS GRID (3 TOP / 2 BOTTOM — PERFECT SPLIT) ===== */
.mk-strengthsGrid{
  display:grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-template-rows: auto auto;
  gap:18px;
  margin-top:24px;
}

/* Top row: 3 cards, each spans 2/6 */
.mk-strengthsGrid .mk-strength:nth-child(1){ grid-column: 1 / span 2; grid-row: 1; }
.mk-strengthsGrid .mk-strength:nth-child(2){ grid-column: 3 / span 2; grid-row: 1; }
.mk-strengthsGrid .mk-strength:nth-child(3){ grid-column: 5 / span 2; grid-row: 1; }

/* Bottom row: 2 cards, each spans half (3/6) */
.mk-strengthsGrid .mk-strength:nth-child(4){ grid-column: 1 / span 3; grid-row: 2; }
.mk-strengthsGrid .mk-strength:nth-child(5){ grid-column: 4 / span 3; grid-row: 2; }

/* Card */
.mk-strength{
  background:#000;
  border:1.5px solid var(--mk-stroke);
  border-radius:16px;
  padding:18px 18px 20px;
  box-shadow: 0 18px 50px rgba(0,0,0,.65);
  text-align:left;

  /* prevents text clipping in grids */
  min-width:0;
}

/* Make sure long text never cuts off */
.mk-strength{
  background:#000;
  border:1.5px solid var(--mk-stroke);
  border-radius:16px;
  padding:18px 18px 20px;
  box-shadow: 0 18px 50px rgba(0,0,0,.65);
  text-align:center;   /* ✅ REQUIRED */
}


/* ===== Responsive ===== */
@media (max-width: 980px){
  .mk-strengthsGrid{
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
  }
  .mk-strengthsGrid .mk-strength{
    grid-column:auto !important;
    grid-row:auto !important;
  }
}

@media (max-width: 640px){
  .mk-strengthsGrid{
    grid-template-columns: 1fr;
  }
}


/* Yellow headline — CENTERED */
.mk-strengthTitle{
  display:inline-flex;              /* key */
  justify-content:center;
  align-items:center;

  margin: 0 auto 10px auto;         /* key */
  padding:6px 12px 5px;

  font-family:"Space Grotesk", Inter;
  font-weight:800;
  font-size:14px;
  letter-spacing:.08em;
  text-transform:uppercase;

  background:#ffd84d;
  color:#000;
  border-radius:10px;
  border:3px solid #050505;
  box-shadow:0 4px 0 #050505;
}


.mk-strengthBody{
  margin:0;
  font:500 14px/1.7 Inter;
  color:var(--muted);
}

/* ===== Responsive ===== */
@media (max-width: 980px){
  .mk-strengthsGrid{
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
  }

  .mk-strengthsGrid .mk-strength{
    grid-column:auto !important;
    grid-row:auto !important;
  }
}

@media (max-width: 640px){
  .mk-strengthsGrid{
    grid-template-columns: 1fr;
  }
}


  .mk-bullets{ width:var(--grid); display:grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 18px; list-style:none; padding:0; margin: 12px auto 0; text-align:left; }
  .mk-bullets li{ display:flex; align-items:flex-start; gap:10px; font: 700 clamp(16px, 1.8vw, 18px)/1.5 Inter; color: var(--text); }
  .dot{ width:9px; height:9px; border-radius:50%; margin-top:8px; flex:none; background: var(--accent); box-shadow: 0 0 10px rgba(29,222,106,.6), 0 0 2px rgba(29,222,106,.9) inset; }
  @media (max-width: 980px){ .mk-bullets{ grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 640px){
    .mk-bullets{ grid-template-columns: 1fr; }
    .mk-nav-inner{ flex-wrap:wrap; justify-content:center; }
  }

  .mk-ctaRow{
  display:flex;
  gap:12px;
  justify-content:center;
  flex-wrap:wrap;
  margin: 34px auto 0;
}
  .mk-btn{ background: var(--accent); color: var(--accent-ink); border:1px solid #1dd86a; padding:12px 16px; border-radius:12px; font: 700 14px/1 Inter; text-decoration:none; }

  .mk-section{ width:var(--grid); margin: 48px auto 0; padding-bottom:6px; text-align:center; overflow-x:hidden; }
  .mk-h2{ font: 800 clamp(22px,3vw,34px)/1.15 "Space Grotesk", Inter; letter-spacing:-.01em; margin:0 0 6px }

  .mk-logoReelWrap{
  margin-top: 48px;
  width: var(--grid);
  margin-left:auto;
  margin-right:auto;
}
  .mk-logoReelFrame{
    position: relative; width: 100%;
    height: clamp(220px, 34vh, 420px);
    border-radius: 18px;
    overflow: hidden;
    background:#000;
    border:1.5px solid var(--mk-stroke);
    box-shadow: 0 22px 70px rgba(0,0,0,.85), 0 0 22px rgba(0,0,0,.75);
  }
  .mk-logoReelFrame video{ position:absolute; inset:0; width:100%; height:100%; object-fit: cover; display:block; }
  .mk-logoReelFrame::after{
    content:""; position:absolute; inset:0;
    background: radial-gradient(80% 80% at 50% 40%, rgba(0,0,0,.10), rgba(0,0,0,.75)),
      linear-gradient(180deg, rgba(0,0,0,.55), rgba(0,0,0,.25) 45%, rgba(0,0,0,.70));
    z-index:1;
  }
  .mk-logoOverlay{ position:absolute; inset:0; z-index:2; display:grid; place-items:center; text-align:center; padding: 18px 16px; }
  .mk-logoOverlayInner{ width:min(920px, 92%); }
  .mk-logoOverlayH2{ margin:0; font: 800 clamp(24px, 3.2vw, 38px)/1.15 "Space Grotesk", Inter; letter-spacing:-.01em; color: var(--text); text-shadow: 0 10px 34px rgba(0,0,0,.65); }
  .mk-logoOverlayP{ margin: 10px auto 0; max-width: 72ch; color: color-mix(in oklab, var(--muted) 82%, white); font: 500 15px/1.7 Inter; text-shadow: 0 10px 30px rgba(0,0,0,.65); }

.mk-brandsRow{
  display:flex;
  flex-wrap:wrap;
  justify-content:center;
  align-items:stretch;
  gap:18px;
  margin-top:20px;
}

.mk-brandCarouselShell{
  position:relative;
  width:100%;
  margin-top:20px;
}

.mk-brandsCarousel{
  display:flex;
  flex-wrap:nowrap;
  justify-content:flex-start;

  overflow-x:auto;
  overflow-y:hidden;

  scroll-snap-type:x mandatory;
  -webkit-overflow-scrolling:touch;

  padding:8px 54px 16px;
  scrollbar-width:none;
}

.mk-brandsCarousel::-webkit-scrollbar{
  display:none;
}

.mk-brandsCarousel .mk-brandCard{
  flex:0 0 clamp(220px, 24vw, 300px);
  scroll-snap-align:center;
}

.mk-brandArrow{
  position:absolute;
  top:50%;
  transform:translateY(-50%);
  z-index:5;
  width:44px;
  height:44px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.18);
  background:rgba(0,0,0,.82);
  color:#fff;
  font:900 34px/1 Inter;
  cursor:pointer;
  box-shadow:0 12px 34px rgba(0,0,0,.65);
}

.mk-brandArrow:hover{
  filter:brightness(1.15);
}

.mk-brandArrowLeft{
  left:4px;
}

.mk-brandArrowRight{
  right:4px;
}

@media (max-width:640px){
  .mk-brandsCarousel{
    padding:8px 44px 16px;
  }

  .mk-brandsCarousel .mk-brandCard{
    flex-basis:78%;
  }

  .mk-brandArrow{
    width:38px;
    height:38px;
    font-size:28px;
  }
}
.brandPopup{
  position: fixed;
  inset: 0;
  z-index: 9999999;
  isolation: isolate;
  background: rgba(0,0,0,.92);
  display: grid;
  place-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 12px calc(12px + env(safe-area-inset-bottom, 0px));
  overflow: hidden;
  overscroll-behavior: contain;
}

.brandPopupInner{
  position: relative;
  z-index: 1;
  width: min(1300px, 96vw);
  height: min(88dvh, 900px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  border-radius: 18px;
  border: 1px solid rgba(255,255,255,.14);
  background: #000;
  box-shadow: 0 30px 120px rgba(0,0,0,.95);
  padding: 18px;
}

.brandPopupClose{
  position: fixed;
  top: calc(18px + env(safe-area-inset-top, 0px));
  right: 18px;
  z-index: 2147483647;
}

  display: flex;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;

  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.28);
  background: rgba(0,0,0,.96);

  color: #fff;
  font: 900 30px/1 Inter;
  cursor: pointer;

  z-index: 10;

  box-shadow: 0 10px 30px rgba(0,0,0,.75);
}

.brandPopupClose:hover{
  filter: brightness(1.15);
}

.brandPopupTop{
  text-align: left;
  margin: 0 0 16px;
}

.brandPopupTitle{
  margin: 0 0 8px;
  font: 900 clamp(22px, 3vw, 34px)/1.1 "Space Grotesk", Inter;
  color: var(--text);
}

.brandPopupBlurb{
  margin: 0;
  max-width: 760px;
  font: 500 14px/1.7 Inter;
  color: var(--muted);
}

.brandPopupGrid{
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.brandPopupMedia{
  border-radius: 14px;
  overflow: hidden;
  background: #050607;
  border: 1px solid #111722;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brandPopupMedia img,
.brandPopupMedia video{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

@media (max-width: 980px){
  .brandPopupGrid{
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px){
  .brandPopup{
    place-items: start center;
    padding: calc(8px + env(safe-area-inset-top, 0px)) 8px calc(8px + env(safe-area-inset-bottom, 0px));
  }

  .brandPopupInner{
    width: 100%;
    height: calc(100dvh - 16px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
    border-radius: 14px;
    padding: 12px;
  }

  .brandPopupClose{
    width: 48px;
    height: 48px;
    font-size: 32px;
  }

  .brandPopupGrid{
    grid-template-columns: 1fr;
  }

  .brandPopupMedia{
    min-height: 260px;
  }
}
  .mk-brandCard{
    background:#000;
    border:1px solid var(--mk-stroke);
    border-radius:16px;
    padding:14px 16px 18px;
    box-shadow:0 18px 50px rgba(0,0,0,.7);
    cursor:pointer;
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:10px;
    width: clamp(220px, 24vw, 320px);
    transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  }
  .mk-brandCard:hover{ transform: translateY(-3px); box-shadow:0 24px 60px rgba(0,0,0,.9); border-color: rgba(255,255,255,.35); }
  .mk-brandCard--active{ border-color:#1dd86a; box-shadow: 0 0 18px rgba(29,222,106,.8), 0 26px 70px rgba(0,0,0,.9); transform: translateY(-4px); }
  .mk-brandLogoFrame{
    width:100%;
    height: clamp(220px, 40vh, 420px);
    border-radius:12px;
    overflow:hidden;
    background:#050607;
    display:flex;
    align-items:center;
    justify-content:center;
  }
  .mk-brandLogoFrame img{ max-width:100%; max-height:100%; object-fit:contain; display:block; }
  .mk-brandName{ font:700 16px/1.3 Inter; color:var(--text); }

  .mk-brandDetailShell{ margin-top:18px; max-width:var(--grid); margin-left:auto; margin-right:auto; overflow-x:hidden; }
  .mk-brandDetail{
    overflow:hidden;
    border-radius:16px;
    border:1px solid var(--mk-stroke);
    background:#020406;
    box-shadow:0 22px 60px rgba(0,0,0,.75);
    max-height:0;
    opacity:0;
    transform: translateY(-6px);
    transition: max-height .45s cubic-bezier(.23,.82,.25,1), opacity .35s ease-out, transform .35s ease-out;
  }
  .mk-brandDetail--open{ max-height:900px; opacity:1; transform: translateY(0); }
  .mk-brandDetailInner{ padding:20px 20px 22px; display:grid; gap:16px; }
  .mk-brandDetailTitle{ font:800 18px/1.3 Inter; margin:0; text-align:left; }
  .mk-brandDetailIntro{ margin:4px 0 10px; font:500 14px/1.7 Inter; color:var(--muted); text-align:left; max-width:640px; }

  .mk-brandMediaRow{ display:flex; flex-wrap:wrap; gap:12px; justify-content:flex-start; }
  .mk-brandMedia{
    flex:1 1 clamp(220px, 26%, 320px);
    border-radius:12px;
    overflow:hidden;
    border:1px solid #111722;
    background:#000;
    height:200px;
    display:flex;
    align-items:center;
    justify-content:center;
  }
  .mk-brandMedia img, .mk-brandMedia video{ width:100%; height:100%; object-fit:cover; display:block; }
  .mk-brandMedia--clickable{ cursor:pointer; }

  .mk-wrap[data-page="portfolio"]{ --outline-yellow: #ffd84d; }
  .mk-wrap[data-page="portfolio"] .ctrlBtn,
  .mk-wrap[data-page="portfolio"] .mk-home,
  .mk-wrap[data-page="portfolio"] .sm-pill,
  .mk-wrap[data-page="portfolio"] .mk-btn{ border-color: var(--outline-yellow); }
  .mk-wrap[data-page="portfolio"] .lamp,
  .mk-wrap[data-page="portfolio"] .dot,
  .mk-wrap[data-page="portfolio"] .sm-pillDot{
    background: var(--outline-yellow);
    box-shadow: 0 0 10px rgba(255,216,77,.75), 0 0 22px rgba(255,216,77,.45);
  }
    /* ==========================
   MOBILE SCALE TWEAKS (HOME)
   ========================== */
@media (max-width: 720px){

  /* 1) Home boot / hero video: zoom out slightly */
  .h_bgVideo{
    transform: scale(.92);
    transform-origin: center center;
  }

  /* (optional) if your boot video uses .videoFill, scale that too */
  .videoFill{
    transform: scale(.92);
    transform-origin: center center;
  }

  /* 2) SCROLL side controls: smaller + less intrusive */
  .h_scroll{
    gap:6px;
    transform: translateY(-50%) scale(.82);
  }

  .h_plate{
    padding:5px 8px;
    border-radius:9px;
  }

  .h_sign{
    font-size: 12px;
    letter-spacing: .14em;
  }

  .h_arrow{
    width:16px;
    height:16px;
    border-right-width:2px;
    border-bottom-width:2px;
  }

  /* keep them tucked closer to the edges */
  .h_scroll.left{ left: 8px; }
  .h_scroll.right{ right: 8px; }
}
  /* ==========================
   MOBILE: move volume control top-left
   ========================== */
@media (max-width: 720px){

  .h_audioWrap{
    top: clamp(10px, 3vw, 14px);
    left: clamp(10px, 3vw, 14px);
    right: auto;
    bottom: auto;

    padding: 6px 8px;
    gap: 8px;
    border-radius: 10px;
  }

  .h_audioBtn{
    font-size: 14px;
    padding: 6px 8px;
  }

  .h_audioSlider{
    width: 90px; /* slightly shorter for mobile */
  }
}


  `;

  return (
    <>
      <style>{css}</style>
      <div className="room">
        <div className="rig">
          <div className="monitor">
            <div className="bezel">
              <span className="screw tl" />
              <span className="screw tr" />
              <span className="screw bl" />
              <span className="screw br" />

              <div className="screen">
                {!powered && showPower ? (
                  <div className="prepower">
                    {bootSrc && (
                      <video
                        className="videoFill"
                        autoPlay
                        muted
                        loop
                        playsInline
                      >
                        <source src={bootSrc} type="video/mp4" />
                      </video>
                    )}
                    <button
                      className="powerOn"
                      onClick={handlePowerOn}
                      aria-label="Power On"
                    >
                      POWER&nbsp;ON
                    </button>
                  </div>
                ) : (
                  <>
                    <nav
                      className="sideDock left"
                      aria-label="Left monitor buttons"
                    >
                      {leftButtons.map((b) => (
                        <button
                          key={b.key}
                          className={`ctrlBtn ${isActive(b.path) ? "active" : ""}`}
                          onClick={() => go(b.path)}
                          aria-disabled={!powered}
                          title={b.label}
                        >
                          <span className="lamp" aria-hidden="true" />
                          <span className="ctrlText">{b.label}</span>
                        </button>
                      ))}
                    </nav>

                    <nav
                      className="sideDock right"
                      aria-label="Right monitor buttons"
                    >
                      {rightButtons.map((b) => (
                        <button
                          key={b.key}
                          className={`ctrlBtn ${isActive(b.path) ? "active" : ""}`}
                          onClick={() => go(b.path)}
                          aria-disabled={!powered}
                          title={b.label}
                        >
                          <span className="lamp" aria-hidden="true" />
                          <span className="ctrlText">{b.label}</span>
                        </button>
                      ))}
                    </nav>

                    <div className="scan" />

                    {mode === "center" ? (
                      <div className="screen-center">{children}</div>
                    ) : (
                      <div ref={scrollRef} className="screen-scroll">
                        {children}
                      </div>
                    )}

                    <ChannelKnob powered={powered} docked={knobDocked} />
                  </>
                )}
              </div>

              <div className={`led ${powered ? "on" : ""}`} />
            </div>

            <div className="stand" />
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================
   HOME CONTENT
   ========================= */
function HomeContent() {
  const [typed, setTyped] = useState("");
  const [fadeWelcome, setFadeWelcome] = useState(false);
  const welcomeText = "Welcome to The Gx Universe";
  const aboutRef = useRef(null);

  const heroVideoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [vol, setVol] = useState(0.6);

  // Lightbox state (HOME)
  const [lbOpen, setLbOpen] = useState(false);
  const [lbSrc, setLbSrc] = useState("");
  const [lbAlt, setLbAlt] = useState("");

  const openLightbox = useCallback((src, alt) => {
    setLbSrc(src);
    setLbAlt(alt || "Preview");
    setLbOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLbOpen(false);
    setLbSrc("");
    setLbAlt("");
  }, []);

  const photoSlides = [
    {
      src: asset("Poster 1.jpg"),
      alt: "GX Poster artwork",
      tag: "ANIMATED SERIES POSTER",
      caption: "Branding the universe before it even launches.",
    },
    {
      src: asset("IMG_4010.jpg"),
      alt: "Recording studio session",
      tag: "GALACTIC STUDIO SESSION: MIAMI",
      caption: "Late–night mix sessions chasing the perfect take.",
    },
    {
      src: asset("green screen studio.jpg"),
      alt: "Green screen studio",
      tag: "FILM STUDIO",
      caption: "Turning a few green walls into whole universes.",
    },
    {
      src: asset("bday studio session.png"),
      alt: "Birthday studio session",
      tag: "GALACTIC STUDIO SESSION: NEW JERSEY",
      caption: "Birthday candles, hard drives, and one more take.",
    },
    {
      src: asset("sxsw.jpg"),
      alt: "SXSW moment",
      tag: "LIVE",
      caption: "From tiny rooms to SXSW energy and real-world stages.",
    },
    {
      src: asset("SPACE MAN.jpg"),
      alt: "Space Man artwork",
      tag: "ON SET BTS",
      caption: "Concept art that turned into a whole storyline.",
    },
  ];

  const [slide, setSlide] = useState(0);
  const nextSlide = () => setSlide((s) => (s + 1) % photoSlides.length);
  const prevSlide = () =>
    setSlide((s) => (s - 1 + photoSlides.length) % photoSlides.length);
  const goToSlide = (i) => setSlide(i);

  useEffect(() => {
    setTyped("");
    setFadeWelcome(false);
    let i = 0;
    const TYPING_MS = 90;
    const FADE_DELAY = 900;

    const tick = () => {
      i += 1;
      setTyped(welcomeText.slice(0, i));
      if (i < welcomeText.length) {
        setTimeout(tick, TYPING_MS);
      } else {
        setTimeout(() => setFadeWelcome(true), FADE_DELAY);
      }
    };

    const id = setTimeout(tick, 220);
    return () => clearTimeout(id);
  }, []);

  const scrollToAbout = () => {
    if (aboutRef.current) {
      aboutRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyScroll = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToAbout();
    }
  };

  const toggleMute = async () => {
    const v = heroVideoRef.current;
    if (!v) return;
    const next = !muted;
    setMuted(next);
    v.muted = next;
    if (!next) {
      try {
        await v.play();
      } catch {
        // ignore autoplay issues
      }
    }
  };

  const handleVol = async (e) => {
    const v = heroVideoRef.current;
    if (!v) return;
    const value = Number(e.target.value);
    setVol(value);
    v.volume = value;
    if (value > 0 && muted) {
      setMuted(false);
      v.muted = false;
      try {
        await v.play();
      } catch {
        // ignore
      }
    }
  };

  const prevIndex = (slide - 1 + photoSlides.length) % photoSlides.length;
  const nextIndex = (slide + 1) % photoSlides.length;

  return (
    <>
      <div className="h_wrap">
        <section className="h_hero">
          <video
            ref={heroVideoRef}
            className="h_bgVideo"
            autoPlay
            loop
            playsInline
            muted={muted}
            onLoadedMetadata={() => {
              if (heroVideoRef.current) heroVideoRef.current.volume = vol;
            }}
          >
            <source src={HOME_HERO_VIDEO} type="video/mp4" />
          </video>

          <div
            className="h_scroll left"
            onClick={scrollToAbout}
            onKeyDown={keyScroll}
            role="button"
            tabIndex={0}
            aria-label="Scroll down"
          >
            <div className="h_plate">
              <div className="h_sign">SCROLL</div>
            </div>
            <div className="h_arrow a1" />
            <div className="h_arrow a2" />
            <div className="h_arrow a3" />
          </div>

          <div
            className="h_scroll right"
            onClick={scrollToAbout}
            onKeyDown={keyScroll}
            role="button"
            tabIndex={0}
            aria-label="Scroll down"
          >
            <div className="h_plate">
              <div className="h_sign">SCROLL</div>
            </div>
            <div className="h_arrow a1" />
            <div className="h_arrow a2" />
            <div className="h_arrow a3" />
          </div>

          <div className="h_overlay">
            <div
              className={`h_welcome ${fadeWelcome ? "fade" : ""}`}
              aria-live="polite"
            >
              {typed}
              <span className="h_cursor">|</span>
            </div>
          </div>

          <div className="h_audioWrap">
            <button
              className="h_audioBtn"
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              title={muted ? "Unmute" : "Mute"}
            >
              {muted ? "🔇" : "🔊"}
            </button>
            <input
              className="h_audioSlider"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={vol}
              onChange={handleVol}
              aria-label="Video volume"
              title="Volume"
            />
          </div>
        </section>

        <div ref={aboutRef} className="h_outer">
          <section
            className="h_block h_block--ink"
            aria-label="Scenes from the Gx Universe"
          >
            <div className="h_centerNote">
              <strong>Scenes from the Gx Universe</strong>
              <p>
                I started in tiny rooms: a green studio where we stitched worlds
                together, a recording booth where we chased the perfect take,
                and late nights rigging lights with a cameraman to make
                something <em>feel</em> alive.
                <br />
                <br />
                <strong>Click a scene to view it larger.</strong>
              </p>
            </div>

            <div className="h_carouselWrap">
              <div className="h_carousel">
                <button
                  type="button"
                  className="h_carBtn"
                  onClick={prevSlide}
                  aria-label="Previous scene"
                >
                  ◀
                </button>

                <div className="h_carFrame h_carFrame--side" aria-hidden="true">
                  <figure className="h_imgCard h_imgCard--carousel h_imgCard--side">
                    <img src={photoSlides[prevIndex].src} alt="" />
                  </figure>
                </div>

                <div className="h_carFrame h_carFrame--center">
                  <figure className="h_imgCard h_imgCard--carousel h_imgClickable">
                    <img
                      src={photoSlides[slide].src}
                      alt={photoSlides[slide].alt}
                      onClick={() =>
                        openLightbox(
                          photoSlides[slide].src,
                          photoSlides[slide].alt,
                        )
                      }
                    />
                    <figcaption className="h_carCaption">
                      <span>{photoSlides[slide].tag}</span>
                      {photoSlides[slide].caption}
                    </figcaption>
                  </figure>
                </div>

                <div className="h_carFrame h_carFrame--side" aria-hidden="true">
                  <figure className="h_imgCard h_imgCard--carousel h_imgCard--side">
                    <img src={photoSlides[nextIndex].src} alt="" />
                  </figure>
                </div>

                <button
                  type="button"
                  className="h_carBtn"
                  onClick={nextSlide}
                  aria-label="Next scene"
                >
                  ▶
                </button>
              </div>

              <div className="h_carDots" aria-label="Carousel slide indicators">
                {photoSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`h_carDot ${i === slide ? "active" : ""}`}
                    onClick={() => goToSlide(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
      <section
        className="h_block h_block--slate"
        aria-label="About The Gx Universe"
      >
        <div className="h_centerNote">
          <p>
            The Gx Universe is a connected world of music, animation, and
            interactive experiences — built like a universe you can actually
            step into.
            <br />
            <br />
            I don’t just produce marketable content. I build scalable worlds —
            continous brand identity, consistent visuals, evolving narratives,
            and assets that deploy fast across platforms.
            <br />
            <br />
            If you’re building a brand, planning a product launch, or need
            someone who can take an idea from concept → execution → rollout,
            you’re in the right place.
            <br />
            <br />
            Tap
            <br />
            Portfolio to see brand work, or Social Media on the channel knob to
            see how it performs in the wild.
          </p>
        </div>
      </section>

      <ImageLightbox
        open={lbOpen}
        src={lbSrc}
        alt={lbAlt}
        onClose={closeLightbox}
      />
    </>
  );
}

/* =========================
   SOCIAL MEDIA (PAGE)
   ========================= */
function SocialMediaContent() {
  useEffect(() => {
    document.title = "Social Media — Gx Universe";
  }, []);

  const IG_POSTS = [
    {
      thumb: "T20 Cover.png",
      url: "https://www.instagram.com/p/C85wWG3Nzoq/",
      tag: "Graphic Design",
      caption: "Hook + cutdowns that keep the drop alive after day one.",
    },
    {
      thumb: "Underground Violet Rave 5.png",
      url: "https://www.instagram.com/reel/C124tqQOUbp/",
      tag: "Large Scale Event Hosting - UVR Miami",
      caption: "Consistent visual system across posts, covers, and typography.",
    },
    {
      thumb: "AR Filters - Space Gliders.png",
      url: "https://www.instagram.com/reel/Chpwzu4AZZu/",
      tag: "AR / FILTERS",
      caption: "Interactive moments that turn viewers into participants.",
    },
    {
      thumb: "animation.png",
      url: "https://www.instagram.com/p/CNSeKKHnPA8/",
      tag: "Animation",
      caption: "Format-native cuts for Reels + feed + stories.",
    },
    {
      thumb: "desert Gx.png",
      url: "https://www.instagram.com/reel/CwAe_eLgC9t/",
      tag: "Marketing / Promotional",
      caption: "Ship, learn, refine — fast feedback loops.",
    },
    {
      thumb: "Recording Studio.png",
      url: "https://www.instagram.com/p/CvKlUXvru1w/",
      tag: "Professional Vocal Recording",
      caption: "Turn one shoot into a month of assets.",
    },
    {
      thumb: "sxsw.jpg",
      url: "https://www.instagram.com/p/CqOR9paPLfu/",
      tag: "Film Festival Entry",
      caption: "Narratives that make people stick around.",
    },
  ];

  const rowRef = useRef(null);

  const scrollByCards = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    const firstCard = el.querySelector(".sm-tile");
    const cardW = firstCard ? firstCard.getBoundingClientRect().width : 260;
    el.scrollBy({ left: dir * (cardW + 12) * 2, behavior: "smooth" });
  };

  // Lightbox state (SOCIAL)
  const [lbOpen, setLbOpen] = useState(false);
  const [lbSrc, setLbSrc] = useState("");
  const [lbAlt, setLbAlt] = useState("");
  const [lbUrl, setLbUrl] = useState("");

  const openTile = (p) => {
    setLbSrc(asset(p.thumb));
    setLbAlt(p.tag);
    setLbUrl(p.url);
    setLbOpen(true);
  };

  const closeTile = () => {
    setLbOpen(false);
    setLbSrc("");
    setLbAlt("");
    setLbUrl("");
  };

  const IG_PROFILE_URL = "https://www.instagram.com/thegxuniverse/";
  const X_PROFILE_URL = "https://x.com/thegxuniverse";

  return (
    <>
      <div className="sm-wrap">
        <section className="sm-hero">
          <div className="sm-hero-inner">
            <h1 className="sm-h1">Social Media</h1>
            <p className="sm-sub">
              Social Media is the distribution layer — supporting launches,
              extending campaigns, and feeding real-world feedback back into
              creative decisions. It’s where we test hooks, find new audiences,
              and keep momentum going after the drop.
            </p>
          </div>
        </section>

        <section className="sm-section" aria-label="Instagram carousel">
          <div className="sm-carouselShell">
            <div className="sm-carouselTop">
              <h2 className="sm-carouselTitle">Instagram Carousel</h2>
              <p className="sm-carouselHint">Tap a tile to view it larger.</p>
            </div>

            <div className="sm-carousel">
              <div ref={rowRef} className="sm-carouselRow" role="list">
                {IG_POSTS.map((p, idx) => (
                  <div
                    key={idx}
                    className="sm-tile"
                    role="listitem"
                    tabIndex={0}
                    onClick={() => openTile(p)}
                    onKeyDown={(e) =>
                      e.key === "Enter" || e.key === " " ? openTile(p) : null
                    }
                    aria-label={`Open preview ${idx + 1}`}
                    title="Click to preview"
                  >
                    <img src={asset(p.thumb)} alt={p.tag} loading="lazy" />
                    <div className="sm-tileOverlay">
                      <div className="sm-tileTag">{p.tag}</div>
                      <p className="sm-tileCap">{p.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sm-actions">
              <button className="sm-btn" onClick={() => scrollByCards(-1)}>
                ◀ Prev
              </button>
              <button
                className="sm-btn sm-btnPrimary"
                onClick={() => scrollByCards(1)}
              >
                Next ▶
              </button>
            </div>
          </div>
        </section>

        <div className="sm-footer" aria-label="Social links">
          <a
            className="sm-pill"
            href={IG_PROFILE_URL}
            target="_blank"
            rel="noreferrer"
          >
            <span className="sm-pillDot" aria-hidden="true" />
            Instagram
          </a>

          <a
            className="sm-pill"
            href={X_PROFILE_URL}
            target="_blank"
            rel="noreferrer"
          >
            <span className="sm-pillDot" aria-hidden="true" />
            Twitter / X
          </a>
        </div>

        <section className="sm-contact" aria-label="Contact">
          <div className="sm-contactCard">
            <h2 className="sm-contactTitle">Contact</h2>
            <p className="sm-contactSub">
              Want to collaborate, hire me, or get a quick walkthrough of the
              work? Hit me up — or grab my resume.
            </p>

            <div className="sm-contactActions">
              <a
                className="sm-contactBtn sm-contactBtnPrimary"
                href={asset("ManinDGauba_Resume.pdf")}
                target="_blank"
                rel="noreferrer"
                aria-label="Download resume"
                title="Download Resume"
              >
                Download Resume
              </a>

              <a
                className="sm-contactBtn"
                href="mailto:ManinDGauba@gmail.com"
                aria-label="Email Manin"
                title="Email"
              >
                Email Me
              </a>
            </div>
          </div>
        </section>
      </div>

      <ImageLightbox
        open={lbOpen}
        src={lbSrc}
        alt={lbAlt}
        onClose={closeTile}
        actionUrl={lbUrl}
        actionLabel="OPEN ON IG"
      />
    </>
  );
}
function BrandPopup({ brand, onClose }) {
  useEffect(() => {
    if (!brand) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.overflow = "hidden";
    document.body.style.userSelect = "none";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [brand, onClose]);

  if (!brand) return null;

  return createPortal(
    <div
      className="brandPopup"
      role="dialog"
      aria-modal="true"
      aria-label={`${brand.name} gallery`}
      onClick={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="brandPopupInner" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="brandPopupClose"
          onClick={onClose}
          aria-label="Close gallery"
        >
          ×
        </button>

        <div className="brandPopupTop">
          <h3 className="brandPopupTitle">{brand.name}</h3>
          <p className="brandPopupBlurb">{brand.blurb}</p>
        </div>

        <div className="brandPopupGrid">
          {brand.projects.map((p, idx) => (
            <div key={idx} className="brandPopupMedia">
              {p.type === "video" ? (
                <video src={p.src} autoPlay loop muted playsInline />
              ) : (
                <img
                  src={p.src}
                  alt=""
                  loading="lazy"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
/* =========================
   MARKETING (PORTFOLIO)
   ========================= */
function MarketingContent() {
  useEffect(() => {
    document.title = "Portfolio — Gx Universe";
  }, []);

  const brandRowRef = useRef(null);

  const scrollBrands = (dir) => {
    const el = brandRowRef.current;
    if (!el) return;

    const firstCard = el.querySelector(".mk-brandCard");
    const cardW = firstCard ? firstCard.getBoundingClientRect().width : 260;

    el.scrollBy({
      left: dir * (cardW + 18) * 2,
      behavior: "smooth",
    });
  };

  const brands = [
    {
      id: "eddie-bauer",
      name: "Eddie Bauer Kids",
      logo: asset("eddie bauer kids logo.png"),
      blurb:
        "Supported e-commerce and seasonal creative for kids outerwear—coordinating assets, assisting on-set workflow, and ensuring content delivered on schedule.",
      projects: [
        {
          type: "image",
          src: asset("black friday 2025 v1.jpg"),
          alt: "Eddie Bauer kids outerwear layout",
        },
        {
          type: "image",
          src: asset("black friday 2025 v3.jpg"),
          alt: "Eddie Bauer kids outerwear layout",
        },
        {
          type: "image",
          src: asset("cyber Monday v1.jpg"),
          alt: "Eddie Bauer kids outerwear layout",
        },
        {
          type: "image",
          src: asset("eb 1.jpg"),
          alt: "Eddie Bauer kids outerwear layout",
        },
        {
          type: "image",
          src: asset("eb 2.jpg"),
          alt: "Eddie Bauer kids lifestyle photography",
        },
        {
          type: "image",
          src: asset("eb 3.jpg"),
          alt: "Eddie Bauer kids lifestyle photography",
        },
        {
          type: "image",
          src: asset("eb 4.jpg"),
          alt: "Eddie Bauer kids lifestyle photography",
        },
        {
          type: "image",
          src: asset("eb 5.jpg"),
          alt: "Eddie Bauer kids lifestyle photography",
        },
        {
          type: "image",
          src: asset("eb 6.jpg"),
          alt: "Eddie Bauer kids lifestyle photography",
        },
      ],
    },
    {
      id: "champion-kids",
      name: "Champion Kids",
      logo: asset("champion kids logo.png"),
      blurb:
        "Contributed to lookbooks and e-commerce imagery—helping maintain organized styling flow, consistent presentation, and clean handoff to post-production.",
      projects: [
        { type: "image", src: asset("champs 1.jpg"), alt: "Champion Kids" },
        { type: "image", src: asset("champs 2.jpg"), alt: "Champion Kids" },
        { type: "image", src: asset("champs 3.jpg"), alt: "Champion Kids" },
      ],
    },
    {
      id: "lands-end",
      name: "Lands' End",
      logo: asset("lands end logo.png"),
      blurb:
        "Produced and organized digital assets for lifestyle apparel—focused on accuracy, consistency across PDPs, and fast turnaround for weekly needs.",
      projects: [
        {
          type: "image",
          src: asset("lands end email ad v2.jpg"),
          alt: "Lands' End email ad v2",
        },
        {
          type: "image",
          src: asset("lands end email ad v3.jpg"),
          alt: "Lands' End email ad v3",
        },
        {
          type: "image",
          src: asset("lands end email ad.jpg"),
          alt: "Lands' End email ad",
        },
        {
          type: "image",
          src: asset("LE shoot 1.jpg"),
          alt: "Lands' End shoot 1",
        },
        {
          type: "image",
          src: asset("LE shoot 2.jpg"),
          alt: "Lands' End shoot 2",
        },
      ],
    },
    {
      id: "caval-cabinetry",
      name: "Caval Cabinetry",
      logo: asset("Caval Cabinetry Logo.png"),
      blurb:
        "Creative marketing, social content, photography direction, visual assets, and digital presentation work for cabinetry, interiors, and home transformation content.",
      projects: [
        { type: "image", src: asset("Caval 1.jpg") },
        { type: "image", src: asset("Caval 2.jpg") },
        { type: "image", src: asset("Caval 3.jpg") },
        { type: "image", src: asset("Caval 4.jpg") },
        { type: "image", src: asset("Caval 5.jpg") },
        { type: "image", src: asset("Caval 6.jpg") },
      ],
    },

    {
      id: "underground-violet-rave",
      name: "Underground Violet Rave",
      logo: asset("Underground Violet Rave Logo.png"),
      blurb:
        "Created and developed the complete visual identity for Underground Violet Rave, including logo design, event branding, promotional graphics, merchandise, digital marketing assets, and creative direction.",

      projects: [
        {
          type: "image",
          src: asset("Underground Violet Rave 1.jpg"),
          alt: "Underground Violet Rave logo",
        },
        {
          type: "image",
          src: asset("Underground Violet Rave 2.png"),
          alt: "Underground Violet Rave branding",
        },
        {
          type: "image",
          src: asset("Underground Violet Rave 3.png"),
          alt: "Underground Violet Rave branding",
        },
        {
          type: "image",
          src: asset("Underground Violet Rave Logo.png"),
          alt: "Underground Violet Rave branding",
        },
        {
          type: "image",
          src: asset("Underground Violet Rave 4.png"),
          alt: "Underground Violet Rave branding",
        },
      ],
    },
  ];

  const [activeBrand, setActiveBrand] = useState(null);

  const openBrand = (brand) => {
    setActiveBrand(brand);
  };

  const closeBrand = () => {
    setActiveBrand(null);
  };
  return (
    <>
      <div id="top" className="mk-wrap" data-page="portfolio">
        <div className="mk-nav">
          <div className="mk-nav-inner">
            <a className="mk-home" href="#top">
              Gx Universe
            </a>
            <div className="mk-spacer" />
            <a className="mk-link" href="#brands">
              Experience
            </a>
            <a className="mk-link" href="#brochures">
              Brochures
            </a>

            <a className="mk-link" href="#pricing">
              Core Strengths
            </a>
            <a className="mk-cta" href="mailto:ManinDGauba@gmail.com">
              Contact
            </a>
          </div>
        </div>

        <section className="mk-hero">
          <div className="mk-hero-inner">
            <h1 className="mk-h1 comicHead">
              Creative Content. Real Growth. Lasting Brands.
            </h1>

            <p className="mk-heroSub">
              A creative media agency helping brands grow through strategy,
              social media, content production, AI-enhanced workflows, and
              data-driven optimization.
            </p>

            <div className="mk-servicesGrid">
              {[
                {
                  title: "Social Media & Brand Growth",
                  items: [
                    "Creative Media Strategy & Management",
                    "Social Media Content Creation & Curation",
                    "Brand Consistency & Identity Alignment",
                  ],
                },
                {
                  title: "Content Production",
                  items: [
                    "Photography & Short-Form Video Production",
                    "Commercial Content Production",
                    "Content Planning & Campaign Execution",
                  ],
                },
                {
                  title: "AI, E-Commerce & Growth",
                  items: [
                    "AI-Enhanced Creative Workflows & Automation",
                    "AI-Powered Content Ideation & Optimization",
                    "E-Commerce Content & Product Marketing",
                  ],
                },
              ].map((group) => (
                <div className="mk-serviceColumn" key={group.title}>
                  <h3 className="mk-serviceTitle">{group.title}</h3>

                  <ul className="mk-bullets">
                    {group.items.map((b) => (
                      <li key={b}>
                        <span className="dot" aria-hidden="true" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mk-ctaRow">
              <a className="mk-btn" href="mailto:ManinDGauba@gmail.com">
                Contact
              </a>
              <a
                className="mk-btn"
                href={asset("ManinDGauba_Resume.pdf")}
                target="_blank"
                rel="noreferrer"
              >
                Download Resume
              </a>
            </div>
          </div>
        </section>

        <section id="brands" className="mk-section">
          <div className="mk-logoReelWrap">
            <div className="mk-logoReelFrame">
              <video
                src={MARKETING_LOGO_REEL}
                autoPlay
                loop
                muted
                playsInline
              />
              <div className="mk-logoOverlay">
                <div className="mk-logoOverlayInner">
                  <h2 className="mk-logoOverlayH2">
                    Selected Brand Experience
                  </h2>
                  <p className="mk-logoOverlayP">
                    Tap a logo to see sample visuals. Click any image to view it
                    larger on the page.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mk-brandCarouselShell">
            <button
              type="button"
              className="mk-brandArrow mk-brandArrowLeft"
              onClick={() => scrollBrands(-1)}
              aria-label="Previous brands"
            >
              ‹
            </button>

            <div className="mk-brandsRow mk-brandsCarousel" ref={brandRowRef}>
              {brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="mk-brandCard"
                  onClick={() => openBrand(b)}
                  aria-label={`Open ${b.name} gallery`}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <div className="mk-brandLogoFrame">
                    <img
                      src={b.logo}
                      alt={b.name}
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>

                  <div className="mk-brandName">{b.name}</div>
                </button>
              ))}
            </div>

            <button
              type="button"
              className="mk-brandArrow mk-brandArrowRight"
              onClick={() => scrollBrands(1)}
              aria-label="Next brands"
            >
              ›
            </button>
          </div>

          <BrandPopup brand={activeBrand} onClose={closeBrand} />
        </section>

        {/* =========================
   {/* =========================
    PROFESSIONAL BROCHURE DESIGN
========================= */}

        <section id="brochures" className="mk-section">
          <h2 className="mk-h2">Professional Brochure Design</h2>

          <p
            style={{
              margin: "8px auto 24px",
              maxWidth: "760px",
              color: "var(--muted)",
              font: "500 16px/1.7 Inter",
            }}
          >
            Professionally designed marketing brochures created to showcase
            products, services, and brand identity through premium layouts,
            photography, and visual storytelling.
          </p>

          <div className="mk-brandsRow">
            <button
              type="button"
              className="mk-brandCard"
              onClick={() =>
                window.open(
                  asset("Copy of Caval Cabinetry Brochure (1).pdf"),
                  "_blank",
                )
              }
            >
              <div className="mk-brandLogoFrame">
                <img
                  src={asset("Caval Cabinetry Brochure Cover.jpg")}
                  alt="Caval Cabinetry Brochure"
                  draggable="false"
                />
              </div>

              <div className="mk-brandName">Caval Cabinetry</div>
            </button>

            <button
              type="button"
              className="mk-brandCard"
              onClick={() =>
                window.open(
                  asset("Lands End Shoratened Catalogue.pdf"),
                  "_blank",
                )
              }
            >
              <div className="mk-brandLogoFrame">
                <img
                  src={asset("Lands End Shoratened Catalogue Cover.jpg")}
                  alt="Lands End Catalogue"
                  draggable="false"
                />
              </div>

              <div className="mk-brandName">Lands End Catalogue</div>
            </button>
            <button
              type="button"
              className="mk-brandCard"
              onClick={() =>
                window.open(asset("Adventures of g & x.pdf"), "_blank")
              }
            >
              <div className="mk-brandLogoFrame">
                <img
                  src={asset("Gx Universe Comic Cover.png")}
                  alt="Lands End Catalogue"
                  draggable="false"
                />
              </div>

              <div className="mk-brandName">Adventures of G & X!</div>
            </button>
          </div>
        </section>

        <section id="pricing" className="mk-section">
          <h2 className="mk-h2">Core Strengths</h2>

          <p
            style={{
              margin: "8px auto 24px",
              maxWidth: "760px",
              color: "var(--muted)",
              font: "500 16px/1.7 Inter",
            }}
          >
            Creative execution backed by structure — built to move fast, stay
            consistent, and scale across platforms.
          </p>

          <div className="mk-strengthsGrid">
            <div className="mk-strength">
              <h3 className="mk-strengthTitle">
                E-commerce Strategy & Performance
              </h3>
              <p className="mk-strengthBody">
                Using analytics to make data-informed decisions across Amazon
                and DTC — analyzing performance, audiences, and trends to evolve
                brand & product strategy.
              </p>
            </div>

            <div className="mk-strength">
              <h3 className="mk-strengthTitle">Project Management</h3>
              <p className="mk-strengthBody">
                Bridging creative, pre-production, post-production, and
                marketing to maintain communication, set milestones, meet
                deadlines, and work with vendors to ensure proper execution.
              </p>
            </div>

            <div className="mk-strength">
              <h3 className="mk-strengthTitle">Visual Systems</h3>
              <p className="mk-strengthBody">
                Consistent brand visuals across PDPs, campaigns, social, and
                launch moments — not one-off assets.
              </p>
            </div>

            <div className="mk-strength">
              <h3 className="mk-strengthTitle">Content That Ships</h3>
              <p className="mk-strengthBody">
                Fast turnaround & structured handoffs with assets deployed
                cleanly across platforms and refined using post-launch trends
                and analytics.
              </p>
            </div>

            <div className="mk-strength">
              <h3 className="mk-strengthTitle">Scalable Execution</h3>
              <p className="mk-strengthBody">
                Processes built for growth, whether it’s one product or an
                entire portfolio.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

/* =========================
   Gallery (PAGE)
   ========================= */
function GalleryContent() {
  useEffect(() => {
    document.title = "Gallery — Gx Universe";
  }, []);

  const MEDIA = [
    {
      id: "home-hero",
      type: "video",
      src: HOME_HERO_VIDEO,
      tag: "Animation",
      title: "Animation Montage Reel",
      span: "cg-span-6",
    },
    {
      id: "logo-reel",
      type: "video",
      src: MARKETING_LOGO_REEL,
      tag: "Animation",
      title: "Logo Animation",
      span: "cg-span-12",
    },

    {
      id: "poster-1",
      type: "image",
      src: asset("Poster 1.jpg"),
      tag: "Marketing",
      title: "Gx Universe Cartoon Poster 1",
      span: "cg-span-4",
    },
    {
      id: "img-4010",
      type: "image",
      src: asset("IMG_4010.jpg"),
      tag: "Recording Studio",
      title: "Community Funded Studio Sessions (Miami)",
      span: "cg-span-4",
    },
    {
      id: "green-screen",
      type: "image",
      src: asset("green screen studio.jpg"),
      tag: "On Set",
      title: "Green Screen Film Studio",
      span: "cg-span-4",
    },
    {
      id: "poster-3",
      type: "image",
      src: asset("Poster 3.png"),
      tag: "Marketing",
      title: "Gx Universe Cartoon Poster 2",
      span: "cg-span-4",
    },

    {
      id: "sxsw",
      type: "image",
      src: asset("sxsw.jpg"),
      tag: "Film  Festivals",
      title: "SXSW Film Festival",
      span: "cg-span-3",
    },
    {
      id: "space-man",
      type: "image",
      src: asset("SPACE MAN.jpg"),
      tag: "On Set Stylist Team",
      title: "Visual Shoot Styling",
      span: "cg-span-3",
    },
    {
      id: "poster-2",
      type: "image",
      src: asset("Poster 2.png"),
      tag: "Marketing",
      title: "Gx Universe Poster 3",
      span: "cg-span-4",
    },

    {
      id: "bday-session",
      type: "image",
      src: asset("bday studio session.png"),
      tag: "Recording Studio",
      title: "Community Funded Studio Sessions (NJ)",
      span: "cg-span-6",
    },
    {
      id: "logo-eb",
      type: "image",
      src: asset("eddie bauer kids logo.png"),
      tag: "Marketing",
      title: "Eddie Bauer Kids Logo",
      span: "cg-span-4",
    },
    {
      id: "logo-champ",
      type: "image",
      src: asset("champion logo.png"),
      tag: "Marketing",
      title: "Champion Logo",
      span: "cg-span-4",
    },
    {
      id: "logo-le",
      type: "image",
      src: asset("lands end logo.jpeg"),
      tag: "Marketing",
      title: "Lands’ End Logo",
      span: "cg-span-4",
    },
    {
      id: "eb-bf3",
      type: "image",
      src: asset("black friday 2025 v3.jpg"),
      tag: "Marketing",
      title: "Black Friday Lands End Ad",
      span: "cg-span-6",
    },
    {
      id: "eb-cm1",
      type: "image",
      src: asset("cyber Monday v1.jpg"),
      tag: "Marketing",
      title: "Cyber Monday Lands End Ad",
      span: "cg-span-6",
    },

    {
      id: "eb-1",
      type: "image",
      src: asset("eb 1.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Eddie Bauer Kids Shoot",
      span: "cg-span-3",
    },
    {
      id: "eb-2",
      type: "image",
      src: asset("eb 2.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Eddie Bauer Kids Shoo",
      span: "cg-span-3",
    },
    {
      id: "eb-3",
      type: "image",
      src: asset("eb 3.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Eddie Bauer Kids Shoot",
      span: "cg-span-3",
    },
    {
      id: "eb-4",
      type: "image",
      src: asset("eb 4.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Eddie Bauer Kids Shoot",
      span: "cg-span-3",
    },
    {
      id: "eb-5",
      type: "image",
      src: asset("eb 5.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Eddie Bauer Kids Shoot",
      span: "cg-span-3",
    },
    {
      id: "eb-6",
      type: "image",
      src: asset("eb 6.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Eddie Bauer Kids Shoot",
      span: "cg-span-3",
    },

    {
      id: "ch-1",
      type: "image",
      src: asset("champs 1.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Champion Kids Shoot",
      span: "cg-span-4",
    },
    {
      id: "ch-2",
      type: "image",
      src: asset("champs 2.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Champion Kids Shoot",
      span: "cg-span-4",
    },
    {
      id: "ch-3",
      type: "image",
      src: asset("champs 3.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Champion Kids Shoot",
      span: "cg-span-4",
    },

    {
      id: "le-email2",
      type: "image",
      src: asset("lands end email ad v2.jpg"),
      tag: "Marketing",
      title: "Lands’ End Email Ad",
      span: "cg-span-6",
    },
    {
      id: "le-email3",
      type: "image",
      src: asset("lands end email ad v3.jpg"),
      tag: "Marketing",
      title: "Lands’ End Email Ad",
      span: "cg-span-6",
    },
    {
      id: "le-email",
      type: "image",
      src: asset("lands end email ad.jpg"),
      tag: "Marketing",
      title: "Lands’ End Email Ad",
      span: "cg-span-4",
    },
    {
      id: "le-shoot1",
      type: "image",
      src: asset("LE shoot 1.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Lands’ End Kids Shoot",
      span: "cg-span-4",
    },
    {
      id: "le-shoot2",
      type: "image",
      src: asset("LE shoot 2.jpg"),
      tag: "Fashion/Apparel Shoots",
      title: "Lands’ End Kids Shoot",
      span: "cg-span-4",
    },
  ];

  const FILTERS = [
    "ALL",
    "Recording Studio",
    "Fashion/Apparel Shoots",
    "Marketing",
    "Animation",
  ];
  const [filter, setFilter] = useState("ALL");
  const [active, setActive] = useState(null);

  const filtered = MEDIA.filter((m) => filter === "ALL" || m.tag === filter);

  const open = (m) => setActive(m);
  const close = () => setActive(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="cg-wrap">
      <section className="cg-hero">
        <div className="cg-hero-inner">
          <h1 className="cg-h1">Gallery</h1>
          <p className="cg-sub">
            Click anything to view it larger. (Videos open with controls.)
          </p>
        </div>
      </section>

      <div className="cg-shell">
        <div className="cg-controls">
          <div
            className="cg-pillRow"
            role="tablist"
            aria-label="Gallery filters"
          >
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={`cg-pill ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
                role="tab"
                aria-selected={filter === f}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="cg-count">
            Showing <strong>{filtered.length}</strong> item
            {filtered.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="cg-grid" aria-label="Gallery media grid">
          {filtered.map((m) => (
            <div
              key={m.id}
              className={`cg-card ${m.span || "cg-span-4"}`}
              onClick={() => open(m)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? open(m) : null
              }
              aria-label={`Open ${m.title}`}
              title="Click to view"
            >
              {m.type === "video" ? (
                <video
                  className="cg-media"
                  src={m.src}
                  muted
                  playsInline
                  autoPlay
                  loop
                />
              ) : (
                <img
                  className="cg-media"
                  src={m.src}
                  alt={m.title}
                  loading="lazy"
                />
              )}

              <div className="cg-overlay">
                <p className="cg-tag">{m.tag}</p>
                <p className="cg-title">{m.title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="cg-lightbox"
          role="dialog"
          aria-modal="true"
          onMouseDown={close}
        >
          <div
            className="cg-lightboxInner"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="cg-lightboxTop">
              <div className="cg-lightboxTopLeft">
                <p className="cg-lightboxKicker">{active.tag}</p>
                <p className="cg-lightboxTitle">{active.title}</p>
              </div>
              <button type="button" className="cg-close" onClick={close}>
                CLOSE
              </button>
            </div>

            <div className="cg-lightboxMedia">
              {active.type === "video" ? (
                <video src={active.src} controls autoPlay playsInline />
              ) : (
                <img src={active.src} alt={active.title} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   ROUTES
   ========================= */
function HomePageBoot() {
  return (
    <CRTLayout showPower bootSrc={HOME_LOGO_VIDEO} mode="scroll">
      <HomeContent />
    </CRTLayout>
  );
}

function HomePageWarm() {
  return (
    <CRTLayout mode="scroll">
      <HomeContent />
    </CRTLayout>
  );
}

function MarketingPage() {
  return (
    <CRTLayout mode="scroll">
      <MarketingContent />
    </CRTLayout>
  );
}

function SocialMediaPage() {
  return (
    <CRTLayout mode="scroll">
      <SocialMediaContent />
    </CRTLayout>
  );
}

function GalleryPage() {
  return (
    <CRTLayout mode="scroll">
      <GalleryContent />
    </CRTLayout>
  );
}

/* =========================
   APP
   ========================= */
export default function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || ""}>
      <Routes>
        <Route path="/" element={<HomePageBoot />} />
        <Route path="/home" element={<HomePageWarm />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/Gallery" element={<GalleryPage />} />
        <Route path="/social-media" element={<SocialMediaPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
